import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/testIds";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Plus } from "lucide-react";
import { Link } from "react-router-dom";

const todayISO = () => new Date().toISOString().slice(0, 10);

const addMonthsISO = (startISO, months) => {
  const d = new Date(startISO);
  const targetMonth = d.getMonth() + months;
  d.setMonth(targetMonth);
  // Handle month overflow (e.g., Jan 31 + 1M => Feb 28/29): if the day rolled forward, snap to last day of intended month
  if (d.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    d.setDate(0);
  }
  return d.toISOString().slice(0, 10);
};

const DURATIONS = [
  { months: 1, label: "1M" },
  { months: 3, label: "3M" },
  { months: 4, label: "4M" },
  { months: 6, label: "6M" },
  { months: 12, label: "12M" },
];

const buildInitialForm = () => ({
  name: "",
  age: 12,
  phone_number: "",
  start_date: todayISO(),
  end_date: addMonthsISO(todayISO(), 1),
  amount_received: "",
  pending_fee: "",
  next_due_date: "",
  received_date: todayISO(),
  third_installment_amount: "",
  third_installment_date: "",
  comments: "",
  coach_id: "",
});

export default function StudentNewPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(buildInitialForm);
  const [duration, setDuration] = useState(1); // months, or null for custom
  const [receivedFollowsStart, setReceivedFollowsStart] = useState(true);
  const [savedInfo, setSavedInfo] = useState(null); // {name, status} shown after success

  useEffect(() => {
    if (isAdmin) {
      api.listCoaches().then(setCoaches).catch(() => {});
    }
  }, [isAdmin]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyDuration = (months) => {
    setDuration(months);
    set("end_date", addMonthsISO(form.start_date, months));
  };

  const onStartChange = (v) => {
    setForm((f) => ({
      ...f,
      start_date: v,
      // If a duration pill is active, auto-recompute end date from new start
      end_date: duration ? addMonthsISO(v, duration) : f.end_date,
      // Auto-track received_date to start_date unless user manually customized it
      received_date: receivedFollowsStart ? v : f.received_date,
    }));
  };

  const onEndChange = (v) => {
    setDuration(null); // manual override — no pill active
    set("end_date", v);
  };

  const onReceivedChange = (v) => {
    setReceivedFollowsStart(false); // user picked a custom received date
    set("received_date", v);
  };

  const resetForNext = () => {
    setSavedInfo(null);
    setForm(buildInitialForm());
    setDuration(1);
    setReceivedFollowsStart(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone_number.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    if (isAdmin && !form.coach_id) {
      toast.error("Please assign a coach");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age) || 0,
        amount_received: Number(form.amount_received) || 0,
        pending_fee: Number(form.pending_fee) || 0,
        next_due_date: form.next_due_date || null,
        received_date: form.received_date || null,
        third_installment_amount: Number(form.third_installment_amount) || 0,
        third_installment_date: form.third_installment_date || null,
        comments: (form.comments || "").slice(0, 800) || null,
        coach_id: isAdmin ? form.coach_id : undefined,
      };
      const s = await api.createStudent(payload);
      if (user?.role === "coach") {
        toast.success("Submitted for admin approval");
        setSavedInfo({ name: s.name, status: "pending" });
      } else {
        toast.success("Student added");
        setSavedInfo({ name: s.name, id: s.id, status: "approved" });
      }
    } catch (err) {
      toast.error(err.message || "Failed to add student");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Link to="/students" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" />
        Back to students
      </Link>
      <h1 className="text-3xl font-extrabold text-slate-900">New Student</h1>

      {savedInfo ? (
        <div
          data-testid="student-saved-success"
          className="bg-slate-50 border border-slate-100 rounded-xl p-6 text-center space-y-4"
        >
          <div className="w-14 h-14 rounded-full bg-[#34C75922] mx-auto flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-[#34C759]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {savedInfo.status === "pending"
                ? `${savedInfo.name} submitted for approval`
                : `${savedInfo.name} added`}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {savedInfo.status === "pending"
                ? "Admin will review and approve shortly."
                : "The student is now visible in the roster."}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              data-testid="add-another-student-btn"
              onClick={resetForNext}
              className="h-11 rounded-lg bg-[#0A2540] hover:bg-[#081D33] text-white font-semibold flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Another Student
            </button>
            <button
              type="button"
              data-testid="back-to-list-btn"
              onClick={() => nav(savedInfo.status === "pending" ? "/dashboard" : "/students")}
              className="h-11 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {savedInfo.status === "pending" ? "Dashboard" : "List"}
            </button>
          </div>
        </div>
      ) : (
      <form onSubmit={submit} className="space-y-4 bg-slate-50 border border-slate-100 rounded-xl p-6">
        <Field label="Name">
          <input
            data-testid={TID.studentNameInput}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="input"
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Age">
            <input
              data-testid={TID.studentAgeInput}
              type="number"
              min={0}
              value={form.age}
              onChange={(e) => set("age", e.target.value)}
              className="input"
              required
            />
          </Field>
          <Field label="Phone">
            <input
              data-testid={TID.studentPhoneInput}
              value={form.phone_number}
              onChange={(e) => set("phone_number", e.target.value)}
              className="input"
              placeholder="+91 ..."
              required
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start Date">
            <input
              data-testid={TID.studentStartInput}
              type="date"
              value={form.start_date}
              onChange={(e) => onStartChange(e.target.value)}
              className="input"
              required
            />
          </Field>
          <Field label="End Date">
            <input
              data-testid={TID.studentEndInput}
              type="date"
              value={form.end_date}
              onChange={(e) => onEndChange(e.target.value)}
              className="input"
              required
            />
          </Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Duration</span>
            {duration === null && (
              <span className="text-xs text-slate-500">Custom end date</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((d) => (
              <button
                key={d.months}
                type="button"
                data-testid={`duration-${d.label}`}
                onClick={() => applyDuration(d.months)}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                  duration === d.months
                    ? "bg-[#0A2540] text-white border-[#0A2540]"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Pick a preset or set a custom End Date above.
          </p>
        </div>
        {/* Installment 1 */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount Received (₹)">
            <input
              data-testid={TID.studentAmountInput}
              type="number"
              min={0}
              step="0.01"
              value={form.amount_received}
              onChange={(e) => set("amount_received", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Received Date">
            <input
              data-testid="student-received-date"
              type="date"
              value={form.received_date}
              onChange={(e) => onReceivedChange(e.target.value)}
              className="input"
            />
          </Field>
        </div>
        {/* Installment 2 */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Pending ₹ (if app)">
            <input
              data-testid={TID.studentPendingInput}
              type="number"
              min={0}
              step="0.01"
              value={form.pending_fee}
              onChange={(e) => set("pending_fee", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Next Due Date (optional)">
            <input
              data-testid={TID.studentDueInput}
              type="date"
              value={form.next_due_date}
              onChange={(e) => set("next_due_date", e.target.value)}
              className="input"
            />
          </Field>
        </div>
        {/* Installment 3 */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Part-3 Amnt ₹">
            <input
              data-testid="student-third-installment-amount"
              type="number"
              min={0}
              step="0.01"
              value={form.third_installment_amount}
              onChange={(e) => set("third_installment_amount", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Part-3 Date (if app)">
            <input
              data-testid="student-third-installment-date"
              type="date"
              value={form.third_installment_date}
              onChange={(e) => set("third_installment_date", e.target.value)}
              className="input"
            />
          </Field>
        </div>
        <Field label="Comments (Optional)">
          <textarea
            data-testid="student-comments"
            rows={3}
            maxLength={800}
            value={form.comments}
            onChange={(e) => set("comments", e.target.value)}
            placeholder="Any notes about this student (max ~100 words)"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm outline-none focus:border-[#0A2540]"
          />
        </Field>
        {isAdmin && (
          <Field label="Assign Coach">
            <select
              data-testid={TID.studentCoachSelect}
              value={form.coach_id}
              onChange={(e) => set("coach_id", e.target.value)}
              className="input"
              required
            >
              <option value="">Select coach</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.full_name} — {c.phone_number}
                </option>
              ))}
            </select>
          </Field>
        )}
        <button
          type="submit"
          data-testid={TID.studentSaveBtn}
          disabled={saving}
          className="w-full h-12 bg-[#0A2540] hover:bg-[#081D33] disabled:opacity-60 text-white rounded-lg font-semibold"
        >
          {saving ? "Saving..." : "Add Student"}
        </button>
      </form>
      )}

      <style>{`
        .input {
          width: 100%; height: 44px; padding: 0 12px;
          border: 1px solid #E5E7EB; border-radius: 8px;
          background: #fff; color: #0F172A; font-size: 14px;
          outline: none;
        }
        .input:focus { border-color: #0A2540; box-shadow: 0 0 0 3px rgba(10,37,64,0.1); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
        {label}
      </span>
      {children}
    </label>
  );
}
